// ./src/components/features/Tags/TagsList/TagsList.tsx
//
// Card-based list for expense tags.
// Same TagCard for all breakpoints:
// - Desktop: hover-to-reveal edit/delete ActionIcons (RecordCard pattern)
// - Mobile: SwipeableCard with swipe-to-reveal actions
//
// Card content: rounded square color swatch + tag name + created date
//
// Context support:
// - When rendered inside an <ExpenseTagsProvider>, data props are read
//   from context automatically. Props can still override context values.

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  ActionIcon,
  Tooltip,
  Skeleton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { ExpenseTag } from "@/utils/api";
import { useOptionalExpenseTagsContext } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import { formatDateTime } from "@/utils";

// ============================================
// Types
// ============================================

export interface TagsListProps {
  /** Array of tag records. Optional when inside provider. */
  tags?: ExpenseTag[];

  /** Loading state for initial load. Optional when inside provider. */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEditTag?: (tag: ExpenseTag) => void;

  /** Callback when delete action is clicked */
  onDeleteTag?: (tag: ExpenseTag) => void;

  /** Callback when a tag card is clicked */
  onTagClick?: (tag: ExpenseTag) => void;

  /** ID of tag currently being deleted. Optional — reads from context. */
  deletingTagId?: string | null;

  /** ID of recently edited/added tag to highlight */
  highlightedTagId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAddTag?: () => void;
}

// ============================================
// CSS for hover-to-reveal actions (RecordCard pattern)
// ============================================

const ACTIONS_CLASS = "tag-card__actions";
const CARD_CLASS = "tag-card";

const CARD_STYLES = `
  .${ACTIONS_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }

  @media (hover: hover) {
    .${CARD_CLASS}:hover .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .${CARD_CLASS}:focus-within .${ACTIONS_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-tag-card", "");
  style.textContent = CARD_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
};

// ============================================
// Loading Skeleton
// ============================================

const TagCardSkeleton: React.FC = () => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <Skeleton height={32} width={32} radius="sm" />
      <Skeleton height={16} width={120} />
      <Box style={{ flex: 1 }} />
      <Skeleton height={14} width={80} />
    </Group>
  </Paper>
);

const LoadingSkeleton: React.FC = () => (
  <Stack gap="sm">
    {Array.from({ length: 5 }).map((_, i) => (
      <TagCardSkeleton key={i} />
    ))}
  </Stack>
);

// ============================================
// TagCard Component
// ============================================

interface TagCardProps {
  tag: ExpenseTag;
  onClick?: (tag: ExpenseTag) => void;
  onEdit?: (tag: ExpenseTag) => void;
  onDelete?: (tag: ExpenseTag) => void;
  highlighted: boolean;
  isDeleting: boolean;
  // Swipe management props (mobile)
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const TagCardComponent: React.FC<TagCardProps> = ({
  tag,
  onClick,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  injectStyles();

  const hasActions = !!onEdit || !!onDelete;

  const handleClick = useCallback(() => {
    onClick?.(tag);
  }, [onClick, tag]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick?.(tag);
      }
    },
    [onClick, tag]
  );

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit?.(tag);
    },
    [onEdit, tag]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(tag);
    },
    [onDelete, tag]
  );

  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => onSwipeStateChange?.(isOpen),
    [onSwipeStateChange]
  );

  // Swipe actions (mobile)
  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(tag),
        disabled: isDeleting,
      });
    }

    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(tag),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, tag, isDeleting, t]);

  // ── Hover actions (desktop) ──
  const hoverActionsEl = hasActions && (
    <Group gap={4} wrap="nowrap" className={ACTIONS_CLASS}>
      {onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={handleEditClick}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={handleDeleteClick}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  // ── Card content ──
  const cardContent = (
    <Paper
      p="md"
      withBorder
      radius="md"
      className={CARD_CLASS}
      opacity={isDeleting ? 0.5 : 1}
      style={{
        cursor: onClick ? "pointer" : undefined,
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        ...(highlighted
          ? {
            borderColor: "var(--mantine-color-teal-5)",
            boxShadow:
              "0 0 0 1px var(--mantine-color-teal-2), 0 4px 16px var(--mantine-color-teal-1)",
          }
          : {}),
      }}
      onClick={onClick ? handleClick : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {/* Color swatch — rounded square */}
        <Box
          style={{
            width: 32,
            height: 32,
            borderRadius: "var(--mantine-radius-sm)",
            backgroundColor: tag.tagColor || "#6B7280",
            flexShrink: 0,
          }}
        />

        {/* Tag name */}
        <Text size="md" fw={500} style={{ flex: 1, minWidth: 0 }} lineClamp={1}>
          {tag.tagName || "—"}
        </Text>

        {/* Hover actions (desktop) */}
        {hoverActionsEl}

        {/* Created date */}
        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {formatDateTime(tag.createdAt, "date")}
        </Text>
      </Group>
    </Paper>
  );

  // Wrap in SwipeableCard on mobile
  if (leftSwipeActions.length > 0) {
    return (
      <>
        {/* Mobile: swipeable */}
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={leftSwipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={handleSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            <Paper
              p="md"
              withBorder
              radius="md"
              opacity={isDeleting ? 0.5 : 1}
              style={{
                cursor: onClick ? "pointer" : undefined,
                transition: "opacity 150ms ease",
                ...(highlighted
                  ? {
                    borderColor: "var(--mantine-color-teal-5)",
                    boxShadow:
                      "0 0 0 1px var(--mantine-color-teal-2), 0 4px 16px var(--mantine-color-teal-1)",
                  }
                  : {}),
              }}
              onClick={onClick ? handleClick : undefined}
              onKeyDown={onClick ? handleKeyDown : undefined}
              tabIndex={onClick ? 0 : undefined}
              role={onClick ? "button" : undefined}
            >
              <Group gap="md" wrap="nowrap" align="center">
                <Box
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "var(--mantine-radius-sm)",
                    backgroundColor: tag.tagColor || "#6B7280",
                    flexShrink: 0,
                  }}
                />
                <Text size="md" fw={500} style={{ flex: 1, minWidth: 0 }} lineClamp={1}>
                  {tag.tagName || "—"}
                </Text>
                <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                  {formatDateTime(tag.createdAt, "date")}
                </Text>
              </Group>
            </Paper>
          </SwipeableCard>
        </Box>

        {/* Desktop: plain card with hover actions */}
        <Box visibleFrom="sm">
          {cardContent}
        </Box>
      </>
    );
  }

  return cardContent;
};

const TagCard = React.memo(TagCardComponent);

// ============================================
// Main Component
// ============================================

const TagsListComponent: React.FC<TagsListProps> = ({
  tags: tagsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditTag,
  onDeleteTag,
  onTagClick,
  deletingTagId: deletingTagIdProp,
  highlightedTagId,
  onAddTag,
}) => {
  const { t } = useTranslation(["tags", "common"]);

  // ── Context vs Props resolution ──
  const tagsContext = useOptionalExpenseTagsContext();

  const tags = tagsProp ?? tagsContext?.expenseTags ?? [];
  const loading = loadingProp ?? tagsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? tagsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? tagsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? tagsContext?.loadMore;
  const deletingTagId = deletingTagIdProp ?? tagsContext?.removingId ?? null;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation
  const { hasAnimated } = useEntranceAnimation({ itemCount: tags.length });

  // ── Wrapped handlers ──

  const handleSwipeStateChange = useCallback(
    (tagId: string, isOpen: boolean) => {
      if (isOpen) openCard(tagId);
    },
    [openCard]
  );

  const handleEdit = useCallback(
    (tag: ExpenseTag) => {
      closeAllCards();
      onEditTag?.(tag);
    },
    [onEditTag, closeAllCards]
  );

  const handleDelete = useCallback(
    (tag: ExpenseTag) => {
      closeAllCards();
      onDeleteTag?.(tag);
    },
    [onDeleteTag, closeAllCards]
  );

  const handleTagClick = useCallback(
    (tag: ExpenseTag) => {
      closeAllCards();
      onTagClick?.(tag);
    },
    [onTagClick, closeAllCards]
  );

  // ── Render card ──

  const renderCard = useCallback(
    (tag: ExpenseTag) => (
      <motion.div
        key={tag.id}
        variants={hasAnimated ? undefined : entranceCardVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        exit="exit"
        layout
      >
        <TagCard
          tag={tag}
          onClick={onTagClick ? handleTagClick : undefined}
          onEdit={onEditTag ? handleEdit : undefined}
          onDelete={onDeleteTag ? handleDelete : undefined}
          highlighted={tag.id === highlightedTagId}
          isDeleting={deletingTagId === tag.id}
          forceClose={shouldForceClose(tag.id || "")}
          onForceCloseComplete={() => handleForceCloseComplete(tag.id || "")}
          onSwipeStateChange={(isOpen) =>
            handleSwipeStateChange(tag.id || "", isOpen)
          }
        />
      </motion.div>
    ),
    [
      onTagClick,
      onEditTag,
      onDeleteTag,
      highlightedTagId,
      deletingTagId,
      hasAnimated,
      shouldForceClose,
      handleForceCloseComplete,
      handleSwipeStateChange,
      handleEdit,
      handleDelete,
      handleTagClick,
    ]
  );

  // Loading state
  if (loading && tags.length === 0) {
    return <LoadingSkeleton />;
  }

  // Empty state
  if (!loading && tags.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("tags:emptyState.title")}
          description={t("tags:emptyState.description")}
          iconName="Tag"
          actionLabel={t("tags:emptyState.actionLabel")}
          onAction={onAddTag}
          size="md"
        />
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-sm)",
        }}
      >
        <AnimatePresence mode="sync">
          {tags.map((tag) => renderCard(tag))}
        </AnimatePresence>
      </motion.div>

      {/* Load more */}
      <LoadMoreButton
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </Stack>
  );
};

export const TagsList = React.memo(TagsListComponent);